<template>
  <el-dialog
    class="update-sample-status-dialog"
    visible
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        isSingleAction
          ? $tc(
              'analysesStatusManagement.groupingStatus.titleSingle',
              samplesId.length,
              {
                action: sampleActions[0].label,
              }
            )
          : $tc(
              'analysesStatusManagement.groupingStatus.title',
              samplesId.length
            )
      "
    />
    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          v-if="!apiCallStarted"
          :gutter="20"
        >
          <el-col :span="24">
            <el-alert
              v-if="!apiCallStarted && message"
              :title="$t('global.warning')"
              :type="messageType"
              :description="message"
              :closable="false"
              show-icon
            >
            </el-alert>

            <el-alert
              v-if="!apiCallStarted && isBulk && !isSingleAction"
              type="info"
              :description="
                $t('analysesStatusManagement.groupingStatus.bulkAlert', {
                  sampleCount: samplesId.length,
                })
              "
              :closable="false"
              show-icon
            >
            </el-alert>
          </el-col>
        </el-row>

        <el-row
          v-if="!apiCallStarted"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('analysesStatusManagement.groupingStatus.status')"
              v-if="!isSingleAction"
            >
              <el-select
                v-model="formData.action"
                :disabled="apiCallStarted"
              >
                <el-option
                  v-for="action in sampleActions"
                  :key="action.value"
                  :label="action.label"
                  :value="action.value"
                />
              </el-select>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          v-if="!apiCallStarted"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              :rules="`${actionReasonRequired ? 'required' : ''}`"
              :label="
                $t('analysesStatusManagement.groupingStatus.actionReason')
              "
            >
              <el-input
                :disabled="apiCallStarted"
                v-model="formData.actionReason"
                type="textarea"
              />
            </FormItem>
          </el-col>
        </el-row>

        <BulkCall
          ref="progress"
          :function="samplesApiExecuteSampleAction"
          :arguments="functionArguments"
          :is-accept="isAccept"
          @completed="bulkCompleted"
        />

        <FormButtons>
          <el-button
            v-if="!apiCallStarted"
            type="secondary"
            :disabled="apiCallStarted"
            @click.prevent="abortAction"
          >
            {{ $t('global.cancel') }}
          </el-button>
          <el-button
            v-if="!apiCallStarted"
            type="primary"
            native-type="submit"
            :disabled="invalid || apiCallStarted"
            :loading="loading"
            @click.prevent="handleSubmit(updateStatus)"
          >
            {{ $t('global.confirm') }}
          </el-button>
          <el-button
            v-if="apiCallStarted && apiCallEnded"
            type="primary"
            @click.prevent="closeBulk"
          >
            {{ $t('global.close') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { SAMPLE_ACTIONS, SAMPLE_ACTIONS_DESC } from '@/config/constants';
  import { samplesApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import BulkCall from '@/components/BulkCall.vue';

  const defaultData = {
    action: null,
    actionReason: '',
  };

  export default {
    name: 'UpdateStatus',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      BulkCall,
    },
    props: {
      samplesId: { type: Array, required: true },
      possibleActions: { type: Object, required: false },
      message: { type: String, required: false },
      messageType: { type: String, required: false },
      autoSubmit: { type: Boolean, default: false },
    },
    data() {
      return {
        formData: { ...defaultData },
        sampleActions: [],
        apiCallStarted: false,
        apiCallEnded: false,
        loading: false,
      };
    },
    computed: {
      samplesApiExecuteSampleAction() {
        return samplesApi.executeSampleAction;
      },
      isBulk() {
        return this.samplesId.length > 1;
      },
      isSingleAction() {
        return this.sampleActions.length === 1;
      },
      actionReasonRequired() {
        const action = this.isSingleAction
          ? this.sampleActions[0].value
          : this.formData.action;
        if (
          action === SAMPLE_ACTIONS.CANCEL ||
          action === SAMPLE_ACTIONS.REJECT ||
          action === SAMPLE_ACTIONS.SUSPEND
        ) {
          return true;
        }
        return false;
      },
      functionArguments() {
        const action = this.isSingleAction
          ? this.sampleActions[0].value
          : this.formData.action;
        return this.samplesId.map((sampleId) => [
          sampleId,
          { action, actionReason: this.formData.actionReason },
        ]);
      },
      isAccept() {
        return this.isSingleAction && this.possibleActions?.ACCEPT === 0;
      },
    },
    async created() {
      const actions = this.possibleActions
        ? this.possibleActions
        : SAMPLE_ACTIONS;
      if (this.isBulk) {
        this.sampleActions = Object.keys(actions).map((key) => ({
          label: this.$t(
            `enums.sampleActions.${SAMPLE_ACTIONS_DESC[SAMPLE_ACTIONS[key]]}`
          ),
          value: SAMPLE_ACTIONS[key],
        }));
      } else {
        try {
          const sampleActionsResponse = await samplesApi.getSampleActions(
            this.samplesId[0]
          );
          const availableActions = Object.entries(actions)
            .reduce((acc, action) => {
              acc.push(action[1]);
              return acc;
            }, [])
            .filter((item) => sampleActionsResponse.data.includes(item));
          this.sampleActions = availableActions.map((value) => ({
            label: this.$t(`enums.sampleActions.${SAMPLE_ACTIONS_DESC[value]}`),
            value,
          }));
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      }
      if (this.isAccept || this.autoSubmit) {
        await this.functionArguments;
        this.updateStatus();
      }
    },
    methods: {
      updateStatus() {
        this.apiCallStarted = true;
        this.$refs.progress.run();
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.$emit('dialogClose', { success: false });
      },
      closeBulk() {
        this.$emit('dialogClose', { success: true });
      },
      bulkCompleted(errors) {
        this.apiCallEnded = true;
        if (!errors) {
          this.closeBulk();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .update-sample-status-dialog {
    .el-dialog {
      width: 30%;
    }

    &__bulk-notice {
      padding-bottom: 1rem;
    }
    .el-alert {
      margin-bottom: 20px;
    }
    .el-dialog__body {
      word-break: break-word;
    }
  }

  .bulk-progress {
    &__title,
    &__error {
      padding: 0.5rem 0;
    }
  }
</style>
